/* eslint-disable import/no-duplicates, no-duplicate-imports, no-param-reassign, arrow-body-style, no-unused-vars, newline-per-chained-call */
import * as API from 'es6/core/api';
import BaseApp from 'es6/controllers/apps/base-app';
import DiagSequencer from 'es6/controllers/apps/diag-sequencer';
import Goal from 'es6/models/goal';
import * as Loaders from 'es6/core/loaders';
import * as Paths from 'es6/core/paths';
import { ItemPackageAssets } from 'es6/core/loaders';
import PlacementScreen from 'es6/components/apps/screens/placement';
import PlacementSessionResultsScreen from 'es6/components/apps/screens/placement-session-results';
import WelcomeScreen from 'es6/components/apps/screens/welcome-diagnostic';

export default class DiagnosticApp extends BaseApp {
  constructor() {
    super('item_study');

    this._screenManager.show('loading');
    this._checkUnsentSaveData(this._fetchData.bind(this));

    // Used for session results and presentation screen.
    this._bands = null;
    this._category = null;
    this._goal = null;
    this._itemPackages = {};
    this._presentationResults = null;
  }

  _setupUtilities() {
    super._setupUtilities();

    // Set volume to 10 for the diagnostic. TODO: Remove this after
    // setting window does not load volume.
    $.setVolume(10);

    Smartfm.Apps.Utilities.ErrorHandling();

    Smartfm.Apps.Modals.dictionary_feedback = new Smartfm.Apps.Utilities.ModalWindow('dictionary_feedback', {
      window: Smartfm.Apps.Common.Windows.dictionaryFeedback,
    });
  }

  _setupScreens() {
    super._setupScreens();
    const screenManager = this._screenManager;

    screenManager.addPanel('bottom');
    screenManager.addPanel('left');

    screenManager.addScreen('loading', Smartfm.Apps.Common.Screens.Loading);

    screenManager.addScreen('recall', Smartfm.Apps.iKnow.Screens.Recall);
    screenManager.addScreen('multiple_choice_five', Smartfm.Apps.iKnow.Screens.MultipleChoice);
    screenManager.addScreen('image_multiple_choice_five', Smartfm.Apps.iKnow.Screens.ImageMultipleChoice);
    screenManager.addScreen('fill_in_the_blank', Smartfm.Apps.iKnow.Screens.FillInTheBlank);

    screenManager.addEvent('session_finished', this._quitSession.bind(this));
  }

  _fetchData() {
    this._loading(true);

    const diagDataLoader = new Loaders.Diagnostic(Smartfm.Apps.Settings.DiagnosticId);

    // Wait for all loaders to finish.
    Promise.all([diagDataLoader, this._setupStaticAssets()])

    // Load the mouseover dictionary.
      .then((loaderResponse) => {
        const mouseoverDictionaryLoader = this._setupMouseoverDictionary(loaderResponse[0].goals);

      // Add it to the loader responses so the study session data gets passed down.
        loaderResponse.push(mouseoverDictionaryLoader);

        return loaderResponse;
      })

    // Unpack data and load assets.
      .then(loaderResponse => {
        this._unpackData(loaderResponse[0]);
        this._loading(false);
      })

      .catch(this._errorHandler.bind(this));
  }

  _unpackData(data) {
    this._screenManager.renderComponent(WelcomeScreen, {
      category: data.diagData.category,
      onCourseDirectory: this._handleCourseDirectory.bind(this),
      onStart: this._startSession.bind(this),
    });

    // Reset the banner.
    this._renderBanner();

    const goals = _.indexBy(data.goals, 'id');
    _.each(data.diagData.bands, (band, index) => {
      // Add study data to each band.
      band.diagStudy = data.diagStudy[index];

      // Add details of the result goals.
      band.diagnosticGoal = goals[band.diagnosticGoalId];

      if (band.diagnosticGoal) {
        // Index the items in the goal by their ids.
        const goalItems = _.indexBy(band.diagnosticGoal.goalItems, ip => {
          return ip.item.id;
        });

        // Add to the item packages lookup.
        _.extend(this._itemPackages, goalItems);

        _.each(band.diagStudy.content, studyNode => {
          studyNode.content = goalItems[studyNode.contentId];
        });
      }
    });

    const diagData = data.diagData;

    this._category = data.diagData.category;
    this._bands = data.diagData.bands;

    // Start asset loading assets in the background.
    const itemPackages = _.chain(data.diagStudy).pluck('content').flatten().pluck('content').value();
    this._assetLoader = new ItemPackageAssets(itemPackages);
  }

  _createSequencer() {
    return new DiagSequencer(this._bands, this._quizTimer);
  }

  /**
   * Called by the sequencer after the sequencer finished. Passes in data, which
   * contains presentation data and placement band returned from the sequencer.
   * Use the band data to look up goal and show the results screen.
   */
  _handleSequencerEnded(data) {
    super._handleSequencerEnded();

    this._presentationResults = data.presentationResults;
    const goalId = this._bands[data.placementBand].resultGoalId;

    this._screenManager.show('loading');

    API.getGoal(goalId)
      .then((goal) => {
        this._goal = new Goal(goal);
        this._showPlacementScreen();
      })
      .catch(this._errorHandler.bind(this));
  }

  _showPlacementScreen() {
    const correctCount = this._presentationResults.filter((pr) => pr.isCorrect()).length;
    const incorrectCount = this._presentationResults.length - correctCount;

    this._screenManager.renderComponent(PlacementScreen, {
      category: this._category,
      correctCount,
      incorrectCount,
      goal: this._goal,
      onCourseDirectory: this._handleCourseDirectory.bind(this),
      onStart: this._handleStartCourse.bind(this),
      onSessionDetails: this._showSessionDetailsScreen.bind(this),
    });

    // Reset the banner.
    this._renderBanner();
  }

  _showSessionDetailsScreen() {
    const results = this._presentationResults.map((pr) => {
      const itemPackage = this._itemPackages[pr.contentId];

      return {
        cue: itemPackage.getText(),
        response: itemPackage.getTranslation(),
        isCorrect: pr.isCorrect(),
      };
    });

    this._screenManager.renderComponent(PlacementSessionResultsScreen, {
      results,
      onBack: this._showPlacementScreen.bind(this),
    });

    // Reset the banner.
    this._renderBanner();
  }

  _handleCourseDirectory() {
    window.location.href = Paths.courseDirectory();
  }

  _handleStartCourse() {
    window.location.href = Paths.studyGoal(this._goal.id);
  }
}
